import { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react'
import type { Toast, ToastVariant } from '../components/Toast'

// Context Types
export interface ToastContextValue {
  toasts: Toast[]
  addToast: (message: string, variant?: ToastVariant, duration?: number) => string
  removeToast: (id: string) => void
  clearToasts: () => void
  // Convenience methods
  success: (message: string, duration?: number) => string
  error: (message: string, duration?: number) => string
  warning: (message: string, duration?: number) => string
  info: (message: string, duration?: number) => string
}

// Default context
const defaultToastContext: ToastContextValue = {
  toasts: [],
  addToast: () => '',
  removeToast: () => {},
  clearToasts: () => {},
  success: () => '',
  error: () => '',
  warning: () => '',
  info: () => '',
}

// Context
export const ToastContext = createContext<ToastContextValue>(defaultToastContext)

// Hook
export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  
  return context
}

// Provider Props
export interface ToastProviderProps {
  children: ReactNode
  /** Default duration in ms (default: 5000) */
  defaultDuration?: number
  /** Maximum toasts to show at once (default: 5) */
  maxToasts?: number
}

// Generate unique ID
function generateId(): string {
  return Math.random().toString(36).substring(2, 9)
}

// Toast Provider Component
export function ToastProvider({ 
  children, 
  defaultDuration = 5000,
  maxToasts = 5,
}: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([])

  // Add toast
  const addToast = useCallback((
    message: string, 
    variant: ToastVariant = 'info',
    duration?: number
  ): string => {
    const id = generateId()
    const toast: Toast = {
      id,
      message,
      variant,
      duration: duration ?? defaultDuration,
    }

    setToasts((prev) => {
      const newToasts = [...prev, toast]
      // Limit max toasts
      if (newToasts.length > maxToasts) {
        return newToasts.slice(-maxToasts)
      }
      return newToasts
    })

    return id
  }, [defaultDuration, maxToasts])

  // Remove toast
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  // Clear all toasts
  const clearToasts = useCallback(() => {
    setToasts([])
  }, [])

  // Convenience methods
  const success = useCallback((message: string, duration?: number) => {
    return addToast(message, 'success', duration)
  }, [addToast])

  const error = useCallback((message: string, duration?: number) => {
    return addToast(message, 'error', duration)
  }, [addToast])

  const warning = useCallback((message: string, duration?: number) => {
    return addToast(message, 'warning', duration)
  }, [addToast])

  const info = useCallback((message: string, duration?: number) => {
    return addToast(message, 'info', duration)
  }, [addToast])

  const contextValue = useMemo<ToastContextValue>(() => ({
    toasts,
    addToast,
    removeToast,
    clearToasts,
    success,
    error,
    warning,
    info,
  }), [toasts, addToast, removeToast, clearToasts, success, error, warning, info])

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  )
}
